<script lang="ts">
  import Seo from '$components/seo.svelte';
  import type { QuizPage } from './content';
  import { cn } from '$lib/utils';
  import Input from '$components/ui/input.svelte';
  import Button from '$components/ui/button.svelte';
  import CollectionDirectory from '$components/directory/collection-directory.svelte';
  import { Dialog } from 'bits-ui';
  import { fade, scale } from 'svelte/transition';
  import { bodyLock } from '$lib/actions/body-lock';
  import Icon from '$components/ui/icon/icon.svelte';
  import type { QuizAnswerStoryblok, QuizQuestionStoryblok } from '$types/bloks';
  import { getImageAttributes } from '$lib/utils/cms';
  export let data: QuizPage;
  import Image from '$components/image.svelte';
  import { snapScrollPosition } from '$lib/actions/snap-scroll-position';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { page } from '$app/stores';
  import { goto } from '$app/navigation';
  import { urlProductFilters } from '$lib/stores/url-product-filters';
  import { onMount } from 'svelte';
  import { createBreakpointMediaQueryStore } from '$lib/stores/media';
  import { DotLottieSvelte } from '@lottiefiles/dotlottie-svelte';
  import DynamicBlock from '$components/blocks/dynamic-block.svelte';
  import Slider from '$components/slider.svelte';
  import { region, type Region } from '$lib/region';
  import { t } from '$lib/i18n';
  import { buildSrcSet } from '$lib/image';
  import { createMutation } from '@tanstack/svelte-query';
  import { toaster } from '$lib/toaster';
  import { elevarAnalyticsEvent, ElevarAnalyticsEvents } from '$lib/analytics';
  import { tieredCampaigns } from '$lib/stores/campaigns';
  let showModal = false;
  let email = '';

  $: theme = data?.story?.content?.theme || 'default';

  interface Question extends QuizQuestionStoryblok {
    answers: Answer[] | undefined;
    disabled: boolean;
  }

  interface Answer extends QuizAnswerStoryblok {
    selected: boolean | undefined;
  }

  let questions: Omit<Question, 'disabled'>[] =
    data.story.content.questions
      ?.filter((q) => q.regions?.includes($region))
      .map((q) => {
        return {
          ...q,
          disabled: q.mandatory,
          answers: q.answers?.map((a: QuizAnswerStoryblok) => {
            return { selected: false, ...a } as Answer;
          })
        };
      }) || [];

  const subcriptionMutation = createMutation<
    unknown,
    unknown,
    { email: string; region: Region; custom_source?: string }
  >({
    mutationKey: ['subscribeNewsletter'],
    mutationFn: async (vars) => {
      const res = await fetch('/api/subscribe/', {
        method: 'POST',
        body: JSON.stringify(vars)
      });

      if (!res.ok) throw new Error('Error subscribing to newsletter');

      return res.json();
    },
    onSuccess: () => {
      elevarAnalyticsEvent({
        event: ElevarAnalyticsEvents.SUBSCRIBE,
        lead_type: 'email',
        user_properties: {
          customer_email: email
        }
      });

      showModal = false;
      toaster({
        type: 'success',
        title: `${$t('newsletter.success.title')}`,
        description: `${$t('newsletter.success.description')}`
      });
    },
    onError: () => {
      toaster({
        type: 'error',
        title: `${$t('newsletter.error.title')}`,
        description: `${$t('newsletter.error.description')}`
      });
    }
  });

  let currIndex = 0;

  $: tieredCampaign =
    $tieredCampaigns && $tieredCampaigns.length > 0 ? $tieredCampaigns[0] : undefined;

  $: quizAnsweredQuestions = questions
    .filter((c) => c.component === 'quiz-question')
    .reduce(
      (acc, q) => {
        acc[q.title] =
          q.answers?.filter((a: Answer) => a.selected)?.map((a: Answer) => a.title) || [];
        return acc;
      },
      {} as Record<string, string[]>
    );

  let submitted = false;
  const submit = () => {
    const filters = questions
      .map((q) => {
        const selectedAnswers = q.answers?.filter((a: Answer) => a.selected == true);
        return selectedAnswers?.map((a: Answer) => a.filters).flat();
      })
      .flat()
      .filter((f) => f != null && f?.length > 0)
      .join('&');

    const emailTab = questions.find((c) => c.component === 'quiz-email');

    if (emailTab && email && isValidEmail(email)) {
      $subcriptionMutation.mutate({
        email: email,
        region: $region,
        custom_source: data.story.content.title
      });
    }
    showModal = false;
    submitted = true;
    goto(`${$page.url.pathname}?${filters}`);

    return;
  };

  // Carousel
  let carousel: HTMLElement;

  const handleCarousel = (
    to: 'left' | 'right' | number,
    behavior: 'instant' | 'smooth' = $reducedMotion ? 'instant' : 'smooth'
  ) => {
    if (to === 'left' && currIndex !== null) {
      carousel.scrollTo({
        left: carousel.clientWidth * (currIndex - 1),
        behavior
      });
    } else if (to === 'right' && currIndex !== null) {
      carousel.scrollTo({
        left: carousel.clientWidth * (currIndex + 1),
        behavior
      });
    } else if (typeof to === 'number') {
      carousel.scrollTo({
        left: carousel.clientWidth * to,
        behavior
      });
    }
  };

  let mounted = false;
  onMount(() => {
    mounted = true;
  });

  // Pattern
  const smBreakpoint = createBreakpointMediaQueryStore('sm');
  const lgBreakpoint = createBreakpointMediaQueryStore('lg');
  const doublexlBreakpoint = createBreakpointMediaQueryStore('2xl');

  let windowHeight = 0;

  $: patternCols = $lgBreakpoint ? 8 : $smBreakpoint ? 8 : 4;
  $: patternRows = Math.round(windowHeight / 86 / 2) * 2;

  $: titleColStart = $doublexlBreakpoint ? patternCols / 2 : patternCols / 2 - 1;
  $: titleColEnd = $doublexlBreakpoint ? titleColStart + 2 : titleColStart + 4;
  $: titleRowStart = patternRows / 2;
  $: titleRowEnd = titleRowStart + 1;

  $: displayPatternImage = (row: number, col: number) => {
    const chessEffect = (row + col) % 2 === 0;

    return chessEffect;
  };

  $: displayChessPattern = (row: number, col: number) => {
    const titleSpace =
      col >= titleColStart - 1 &&
      col <= titleColEnd - 2 &&
      row >= titleRowStart - 1 &&
      row <= titleRowEnd - 1;

    return !titleSpace;
  };

  // Modal
  $: getImageName = (row: number, col: number) => {
    if (theme === 'eyes') {
      return 'eye_' + ((Math.floor((col + row * patternCols) / 2) % 3) + 1);
    } else {
      return (Math.floor((col + row * patternCols) / 2) % 30) + 1;
    }
  };

  const getRandomNumber = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1) + min);
  };

  $: getQuestionColumns = (length: number) => {
    if ($lgBreakpoint) {
      if (length < 3) {
        return length;
      }

      if (length % 5 == 0) return 5;
      if (length % 3 == 0) return 3;
      if (length % 2 == 0) return 4;

      return 4;
    } else {
      if (length == 4 || length == 2) {
        return 2;
      }
      if (length <= 5) {
        return 1;
      }

      if (length % 5 == 0) return 2;
      if (length % 2 == 0) return 2;

      return 2;
    }
  };

  const handleAnswerChange = (answer: Answer) => {
    submitted = false;
    //Update disabled state
    if (answer.selected) {
      questions[currIndex].disabled = false;
      if (questions[currIndex].single_answer) {
        questions[currIndex].answers?.forEach((a: Answer) => {
          if (a._uid !== answer._uid) {
            a.selected = false;
          }
        });
        handleCarousel('right');
      }
    } else {
      questions[currIndex].disabled = !questions[currIndex].answers?.find(
        (a: Answer) => a.selected == true
      );
    }
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
</script>

<Seo
  title={data.story.content.seo_title}
  description={data.story.content.seo_description}
  image={data.story.content.seo_og_image}
/>
<svelte:window bind:innerHeight={windowHeight} />
<Dialog.Root
  preventScroll={false}
  portal={null}
  open={showModal}
  onOpenChange={(open) => (showModal = open)}
>
  <Dialog.Portal asChild>
    <Dialog.Overlay
      transition={fade}
      transitionConfig={{ duration: 150 }}
      class="fixed inset-0 z-popover bg-primary/40"
    />
    <Dialog.Content
      transition={scale}
      transitionConfig={{ start: 0.5, duration: 300 }}
      class={cn(
        'fixed left-[50%] top-[50%] z-popover',
        'translate-x-[-50%] translate-y-[-50%]',
        'outline-none w-full container',
        'px-4 lg:px-8 2xl:px-0',
        'text-highlight'
      )}
    >
      <div class="rounded-[16px] bg-primary" use:bodyLock>
        <!-- Header -->
        <Dialog.Title class="flex flex-col">
          <div class="flex justify-between flex-row py-3 px-4 lg:py-6 lg:px-5">
            <div class="flex gap-1.5 items-center">
              <div class="h-5 w-5">
                <DotLottieSvelte
                  src={'/assets/lotties/sparkles.json'}
                  loop
                  autoplay
                  mode="bounce"
                  renderConfig={{ devicePixelRatio: 2 }}
                />
              </div>
              <h1 class="text-xl">{questions[currIndex].title}</h1>
            </div>

            <button
              class="flex h-fit focus-within:outline-none"
              on:click={() => {
                showModal = false;
              }}
            >
              <Icon size="lg" icon="cross" />
            </button>
          </div>
          <div
            class={cn(
              'w-full min-h-[23px]',
              'pl-3 lg:pl-6 pb-2',
              'font-primary text-sm',
              'text-highlight/64'
            )}
          >
            {#if questions[currIndex].component === 'quiz-question'}
              {#if questions[currIndex].single_answer}
                {$t('quiz.question.single-answer')}
              {:else}
                {$t('quiz.question.multi-answer')}
              {/if}
            {/if}
          </div>
        </Dialog.Title>

        <!-- Content -->
        <div
          class={cn(
            'flex h-full w-full',
            'snap-x snap-mandatory',
            'overflow-x-auto hide-scrollbar',
            'motion-reduce:transition-none'
          )}
          bind:this={carousel}
          use:snapScrollPosition={(i) => (currIndex = i)}
          on:wheel={(event) => {
            if (questions[currIndex].disabled && event.deltaX > 0) event.preventDefault();
          }}
          on:touchmove={(event) => {
            if (
              questions[currIndex].disabled &&
              event instanceof TouchEvent &&
              event.touches[0].clientX < event.targetTouches[0].clientX
            ) {
              event.preventDefault();
            }
          }}
        >
          {#each questions || [] as question}
            <div
              class={cn(
                'h-full min-w-full overflow-y-auto snap-center snap-always',
                'transform-gpu transition-transform duration-500 motion-reduce:transition-none'
              )}
              style:--questionCols={`repeat(${getQuestionColumns((question.answers || []).length)}, minmax(0, 1fr))`}
            >
              <div class="grid grid-cols-[--questionCols] lg:gap-4 gap-2 px-3 lg:px-6 h-[478px]">
                {#if question.component === 'quiz-question'}
                  {#each question.answers || [] as answer}
                    <div class="min-h-[50px] select-none">
                      <input
                        type="checkbox"
                        id={answer._uid}
                        value={answer.filters}
                        bind:checked={answer.selected}
                        on:change={() => handleAnswerChange(answer)}
                        class="hidden peer"
                      />
                      <label
                        for={answer._uid}
                        class={cn(
                          'relative block h-full',
                          'cursor-pointer',
                          'border border-highlight/14 rounded-md',
                          'peer-checked:border peer-checked:border-accent',
                          'overflow-hidden',
                          'p-0.5 lg:p-1',
                          'text-xl',
                          'transition-colors'
                        )}
                      >
                        <div
                          class="absolute h-full w-full inset-0 z-20 flex items-center justify-center"
                        >
                          <p class="text-center">
                            {answer.title}
                          </p>
                        </div>

                        <div class="relative w-full h-full overflow-hidden rounded-xs">
                          <div
                            class={cn(
                              'absolute inset-0 bg-accent/8 w-full h-full z-10  transition-all',
                              answer.selected ? 'bg-accent/8' : 'bg-primary/8'
                            )}
                          ></div>

                          {#if answer.image && answer.image?.filename}
                            <Image
                              loading="eager"
                              class="h-full w-full object-cover overflow-hidden rounded-xs"
                              srcset={buildSrcSet({
                                loader: ({ width }) =>
                                  getImageAttributes(answer.image, { size: [width, 0] }).src,
                                widths: [320, 640]
                              })}
                            />
                          {/if}
                        </div>
                      </label>
                    </div>
                  {/each}
                {:else}
                  <div class="h-full flex justify-center items-center">
                    <div class="max-w-[340px] sm:max-w-[500px] flex flex-col gap-10">
                      <span class="text-xl">{question.description}</span>
                      <div class="flex flex-col gap-3">
                        <p class="text-sm text-highlight/64">
                          {$t('quiz.email.title')}
                        </p>

                        <div class="relative w-full">
                          <Input
                            type="email"
                            bind:value={email}
                            on:input={() => {
                              questions[currIndex].disabled =
                                question.mandatory && (!email || !isValidEmail(email));
                            }}
                            placeholder={$t('pdp.out-of-stock.placeholder')}
                            class="w-full"
                            inputClass={cn(
                              'border-highlight/14',
                              'text-highlight',
                              'focus:border-accent',
                              'placeholder:text-highlight/64',
                              '[&:not(:placeholder-shown):not(:focus)]:border-highlight/14',
                              question.mandatory && 'pr-24'
                            )}
                          />
                          {#if question.mandatory}
                            <div
                              class="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-highlight/64"
                            >
                              {$t('quiz.email.mandatory')}
                            </div>
                          {/if}
                        </div>
                      </div>
                    </div>
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>

        <!-- Footer -->
        <div class="grid grid-cols-3 px-3 py-3 lg:py-5 lg:px-6">
          {#if currIndex !== 0}
            <button
              class={cn(
                'flex justify-center items-center',
                'w-fit h-8',
                'col-start-1 col-end-2',
                'p-1',
                'bg-highlight/8',
                'rounded-sm'
              )}
              on:click={() => handleCarousel('left')}
            >
              <Icon size="md" icon="chevron-left-bold" />
              <span class="pr-1 text-sm">{$t('quiz.back')}</span>
            </button>
          {/if}

          <Slider
            total={questions.length}
            currentIndex={currIndex}
            variant="quiz"
            class="col-start-2 col-end-3 justify-self-center self-center pointer-events-none "
          />

          {#if currIndex !== questions.length - 1}
            <button
              class={cn(
                'flex justify-center items-center w-fit h-8 p-1',
                'rounded-sm bg-accent text-highlight hover:bg-accent/96',
                'col-start-3 col-end-4 justify-self-end',
                'transition-all duration-700 ease-in-out',
                'disabled:opacity-50 disabled:pointer-events-none',
                'disabled:bg-highlight/8 disabled:hover:bg-accent',
                questions[currIndex].single_answer && questions[currIndex].mandatory && 'invisible'
              )}
              disabled={questions[currIndex].disabled}
              on:click={() => handleCarousel('right')}
            >
              <span class="pl-1 text-sm">{$t('quiz.next')}</span>
              <Icon size="md" icon="chevron-right-bold" />
            </button>
          {/if}

          {#if currIndex === questions.length - 1}
            <Button
              variant="accent"
              size="xs"
              class={cn(
                'w-fit col-start-3 col-end-4 justify-self-end',
                'transition-all duration-700 ease-in-out',
                'disabled:opacity-50 disabled:pointer-events-none',
                'disabled:bg-highlight/8 disabled:hover:bg-accent'
              )}
              disabled={questions[currIndex].disabled}
              loading={$subcriptionMutation.isLoading}
              on:click={() => submit()}>{data.story.content.modal_submit_cta}</Button
            >
          {/if}
        </div>
      </div>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>

{#if $urlProductFilters?.length == 0}
  <div
    class="relative w-full h-full overflow-hidden"
    style:--patternCols={`repeat(${patternCols}, minmax(0, 1fr))`}
    style:--titleColStart={titleColStart}
    style:--titleColEnd={titleColEnd}
    style:--titleRowStart={titleRowStart}
    style:--titleRowEnd={titleRowEnd}
  >
    {#if mounted}
      <div class={cn('grid grid-cols-[--patternCols] w-auto', theme === 'eyes' && 'bg-[#f4efe2]')}>
        <h1
          class={cn(
            'text-[40px] lg:text-[48px] font-semibold text-center',
            'row-start-[--titleRowStart] row-end-[--titleRowEnd]',
            'col-start-[--titleColStart] col-end-[--titleColEnd]',
            'mt-auto mb-8'
          )}
        >
          {data.story.content.title}
        </h1>

        <div
          transition:scale|local={{ start: 0.7, duration: 500 }}
          class={cn(
            'row-start-[calc(var(--titleRowStart)+1)] row-end-[--titleRowEnd]',
            'col-start-[--titleColStart] col-end-[--titleColEnd]',
            'mx-auto'
          )}
        >
          <Button
            size="md"
            class="h-12 rounded-[16px]"
            on:click={() => {
              currIndex = 0;
              showModal = true;
            }}
          >
            <div class="flex flex-row gap-1.5 items-center">
              <div class="h-5 w-5">
                <DotLottieSvelte
                  src={'/assets/lotties/sparkles.json'}
                  loop
                  autoplay
                  mode="bounce"
                  renderConfig={{ devicePixelRatio: 2 }}
                />
              </div>
              {data.story.content.modal_trigger_cta}
            </div>
          </Button>
        </div>

        {#each Array.from({ length: patternRows }) as _, rowIndex}
          {#each Array.from({ length: patternCols }) as _, colIndex}
            {#if displayChessPattern(rowIndex, colIndex)}
              <div
                class={'w-[86px] h-[86px] m-auto'}
                style:--floater-jump={`${getRandomNumber(5, 10)}%`}
                style:--floater-duration={`${getRandomNumber(6500, 8500)}ms`}
                style:--image-width={`${getRandomNumber(40, 86)}px`}
              >
                {#if displayPatternImage(rowIndex, colIndex)}
                  <img
                    src={`/assets/quiz-pattern-images/${getImageName(rowIndex, colIndex)}.png`}
                    alt="jewelry item"
                    class={cn(
                      'select-none animate-floater transition ease',
                      theme === 'eyes' && 'w-[var(--image-width)]'
                    )}
                    draggable="false"
                  />
                {/if}
              </div>
            {/if}
          {/each}
        {/each}
      </div>
    {:else}
      <div class="w-full h-[100svh]"></div>
    {/if}
  </div>
{:else}
  <div class="relative">
    {#if data.story.content.target_collection}
      <div>
        <CollectionDirectory
          collectionName={data.story.content.target_collection}
          collectionHandle={data.story.content.target_collection}
          pageSize={30}
          hidePromotionalBlocks={true}
          quizData={submitted && email && isValidEmail(email)
            ? {
                title: data.story.content.title,
                email,
                answers: Object.entries(quizAnsweredQuestions).map(([question, answers]) => ({
                  [question]: answers
                }))
              }
            : undefined}
        />

        <div
          class={cn(
            'w-full flex sticky justify-center z-10 flex-col items-center gap-2',
            tieredCampaign ? 'bottom-[48px]' : 'bottom-4'
          )}
        >
          <Button
            size="md"
            icon={'sparkles'}
            on:click={() => {
              currIndex = 0;
              showModal = true;
            }}
          >
            {data.story.content.modal_trigger_cta}
          </Button>
        </div>
      </div>

      {#if data.story.content.body}
        {#each data.story.content.body as b}
          <DynamicBlock block={b} />
        {/each}
      {/if}
    {/if}
  </div>
{/if}
